import { derived, get, type Readable, writable } from 'svelte/store';

import {
    EffectiveInternetType,
    InternetQuality,
    InternetType,
    type NetworkInformation,
    type NetworkInformationStore,
    type NetworkQualityStoreState,
} from './types';

const initialValues: NetworkInformation = {
    downlink: -1,
    effectiveType: EffectiveInternetType.Unknown,
    isOnline: true,
    rtt: -1,
    type: InternetType.Unknown,
};

function createStore(): NetworkInformationStore {
    const store = writable(initialValues);
    const { subscribe, update, set } = store;

    return {
        get() {
            return get(store);
        },
        isSupported() {
            const state = get(store);
            return !(
                state.downlink === -1 &&
                state.effectiveType === EffectiveInternetType.Unknown &&
                state.type === InternetType.Unknown &&
                state.rtt === -1 &&
                state.isOnline === true
            );
        },
        update,
        set,
        subscribe,
        setInternetMetrics(networkMetrics: NetworkInformation) {
            const lastNetworkStatus = get(store);
            const isUpdated =
                lastNetworkStatus.effectiveType !== networkMetrics.effectiveType ||
                lastNetworkStatus.downlink !== networkMetrics.downlink ||
                lastNetworkStatus.rtt !== networkMetrics.rtt ||
                lastNetworkStatus.isOnline !== networkMetrics.isOnline;
            if (isUpdated) {
                set(networkMetrics);
            }
        },
    };
}

export const networkInformationStore = createStore();

export const networkQualityStore: Readable<NetworkQualityStoreState> =
    derived([networkInformationStore], ([$networkInformationStatus]) => {
        if (!networkInformationStore.isSupported()) {
            return {
                quality: InternetQuality.NotSupported,
            };
        }
        if (!$networkInformationStatus.isOnline) {
            return {
                quality: InternetQuality.Offline,
            };
        }
        if ($networkInformationStatus.downlink < 3) {
            return {
                quality: InternetQuality.SmallBandwith,
            };
        }

        if ($networkInformationStatus.rtt > 100 && $networkInformationStatus.rtt < 200) {
            return {
                quality: InternetQuality.SlowNetwork,
            };
        }

        if ($networkInformationStatus.rtt > 300) {
            return {
                quality: InternetQuality.VerySlowNetwork,
            };
        }
        return {
            quality: InternetQuality.Good,
        };
    });